import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from 'hooks/usePermissions';
import { Users, CheckCircle, XCircle, Clock, Filter, Search, Settings, Shield, ChevronDown } from 'lucide-react';
import api from 'services/api';
import Modal from 'components/common/Modal';
import PermissionSelector from 'components/common/PermissionSelector';
import PermissionSummary from 'components/common/PermissionSummary';
import NotificationModal from 'components/common/NotificationModal';
import PageLayout from 'components/common/PageLayout';
import permissionService from 'features/administrativo/services/permissionService';

const GestaoSolicitacoes = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('pending');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [dropdownAberto, setDropdownAberto] = useState(null);
  
  // Estados para os modais
  const [modalAprovar, setModalAprovar] = useState(false);
  const [modalRejeitar, setModalRejeitar] = useState(false);
  const [modalPermissoes, setModalPermissoes] = useState(false);
  const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [loadingAcao, setLoadingAcao] = useState(false);
  
  // Estados para permissões
  const [permissoesSelecionadas, setPermissoesSelecionadas] = useState([]);
  const [empresasSelecionadas, setEmpresasSelecionadas] = useState([]);
  const [loadingPermissoes] = useState(false);
  
  // Estado para armazenar permissões por solicitação
  const [permissoesPorSolicitacao, setPermissoesPorSolicitacao] = useState({});
  
  // Estado para notificações
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });

  const carregarSolicitacoes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/access-requests?status=${filtroStatus}&page=${pagina}&per_page=15`);
      setSolicitacoes(response.data.data || []);
      setTotalPaginas(response.data.last_page || 1);
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
    } finally {
      setLoading(false);
    }
  }, [filtroStatus, pagina]);

  useEffect(() => {
    carregarSolicitacoes();
  }, [carregarSolicitacoes]);

  const toggleDropdown = (campo) => {
    setDropdownAberto(dropdownAberto === campo ? null : campo);
  };

  const handleFiltroChange = (campo, valor) => {
    if (campo === 'status') {
      setFiltroStatus(valor);
    }
    setDropdownAberto(null);
  };

  // Fechar dropdowns quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownAberto && !event.target.closest('.relative')) {
        setDropdownAberto(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownAberto]);

  const abrirModalAprovar = (solicitacao) => {
    setSolicitacaoSelecionada(solicitacao);
    
    // Carregar permissões específicas desta solicitação para o modal de aprovação
    const solicitacaoId = solicitacao.id;
    const permissoesSalvas = permissoesPorSolicitacao[solicitacaoId] || { permissoes: [], empresas: [] };
    
    setPermissoesSelecionadas(permissoesSalvas.permissoes);
    setEmpresasSelecionadas(permissoesSalvas.empresas);
    setModalAprovar(true);
  };

  const abrirModalRejeitar = (solicitacao) => {
    setSolicitacaoSelecionada(solicitacao);
    setMotivoRejeicao('');
    setModalRejeitar(true);
  };

  const abrirModalPermissoes = (solicitacao) => {
    setSolicitacaoSelecionada(solicitacao);
    
    // Carregar permissões específicas desta solicitação se existirem
    const solicitacaoId = solicitacao.id;
    const permissoesSalvas = permissoesPorSolicitacao[solicitacaoId] || { permissoes: [], empresas: [] };
    
    setPermissoesSelecionadas(permissoesSalvas.permissoes);
    setEmpresasSelecionadas(permissoesSalvas.empresas);
    setModalPermissoes(true);
  };

  const fecharModais = () => {
    setModalAprovar(false);
    setModalRejeitar(false);
    setModalPermissoes(false);
    setSolicitacaoSelecionada(null);
    setMotivoRejeicao('');
    setLoadingAcao(false);
    // Não limpar permissões e empresas ao fechar modais
    // setPermissoesSelecionadas([]);
    // setEmpresasSelecionadas([]);
  };

  const handlePermissoesChange = (permissoes, empresas) => {
    setPermissoesSelecionadas(permissoes);
    setEmpresasSelecionadas(empresas);
    
    // Salvar permissões específicas para esta solicitação
    if (solicitacaoSelecionada) {
      const solicitacaoId = solicitacaoSelecionada.id;
      setPermissoesPorSolicitacao(prev => ({
        ...prev,
        [solicitacaoId]: { permissoes, empresas }
      }));
    }
  };

  const limparPermissoes = () => {
    
    // Limpar estado atual
    setPermissoesSelecionadas([]);
    setEmpresasSelecionadas([]);
    
    // Limpar também as permissões salvas para esta solicitação
    if (solicitacaoSelecionada) {
      const solicitacaoId = solicitacaoSelecionada.id;
      setPermissoesPorSolicitacao(prev => ({
        ...prev,
        [solicitacaoId]: { permissoes: [], empresas: [] }
      }));
    }
    
    // Mostrar notificação de confirmação
    showNotification('info', 'Seleções Limpas', 'Todas as permissões e empresas foram desmarcadas.');
  };

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  const aprovarSolicitacao = async () => {
    if (!solicitacaoSelecionada) return;
    
    try {
      setLoadingAcao(true);
      
      // 1. Aprovar a solicitação
      // Se há empresas selecionadas, usar o company_id da primeira empresa
      const companyId = empresasSelecionadas.length > 0 ? empresasSelecionadas[0].id : null;
      
      await api.post(`/access-requests/${solicitacaoSelecionada.id}/approve`, {
        role_id: 1, // Usar role padrão (admin) - será definido pelo administrador
        company_id: companyId, // Enviar company_id selecionado
        send_credentials: true
      });
      
      // 2. Se há permissões ou empresas selecionadas, aplicá-las ao usuário
      if (permissoesSelecionadas.length > 0 || empresasSelecionadas.length > 0) {
        try {
          // Buscar o usuário criado pela aprovação
          const userResponse = await api.get(`/users?email=${solicitacaoSelecionada.corporate_email}`);
          if (userResponse.data.data && userResponse.data.data.length > 0) {
            const user = userResponse.data.data[0];
            
            // Aplicar permissões se houver
            if (permissoesSelecionadas.length > 0) {
              await permissionService.assignPermissions(user.id, permissoesSelecionadas);
            }
            
            // Associar usuário às empresas selecionadas
            if (empresasSelecionadas.length > 0) {
              await permissionService.assignUserToCompanies(user.id, empresasSelecionadas);
            }
          }
        } catch (permError) {
          console.error('❌ Erro ao aplicar permissões/empresas:', permError);
          // Não falhar a aprovação por causa das permissões
        }
      }
      
      carregarSolicitacoes();
      
      // Limpar permissões específicas desta solicitação após aprovação
      if (solicitacaoSelecionada) {
        const solicitacaoId = solicitacaoSelecionada.id;
        setPermissoesPorSolicitacao(prev => {
          const newState = { ...prev };
          delete newState[solicitacaoId];
          return newState;
        });
      }
      
      limparPermissoes(); // Limpar permissões atuais
      fecharModais();
      showNotification('success', 'Solicitação Aprovada', 'Solicitação aprovada com sucesso! Credenciais enviadas por e-mail.');
    } catch (error) {
      console.error('Erro ao aprovar:', error);
      
      // Verificar se é erro de solicitação já aprovada
      if (error.response?.data?.message?.includes('já foi aprovada')) {
        showNotification('warning', 'Solicitação Já Aprovada', 'Esta solicitação já foi aprovada anteriormente.');
        carregarSolicitacoes(); // Recarregar para atualizar status
      } else if (error.response?.data?.message?.includes('já foi rejeitada')) {
        showNotification('warning', 'Solicitação Já Rejeitada', 'Esta solicitação já foi rejeitada anteriormente.');
        carregarSolicitacoes(); // Recarregar para atualizar status
      } else if (
        error.response?.data?.message?.includes('Já existe um usuário com este email')
        || error.response?.data?.message?.includes('Já existe um usuário com este e-mail')
      ) {
        showNotification('warning', 'E-mail Já Cadastrado', 'Já existe um usuário com este e-mail. Verifique a lista de usuários.');
        carregarSolicitacoes(); // Recarregar para atualizar status
      } else if (error.response?.data?.message?.includes('Erro ao aprovar solicitação')) {
        showNotification('error', 'Erro no Servidor', 'Ocorreu um erro interno. Tente novamente ou contate o suporte.');
        carregarSolicitacoes(); // Recarregar para atualizar status
      } else {
        showNotification('error', 'Erro na Aprovação', 'Erro ao aprovar solicitação. Tente novamente.');
      }
    } finally {
      setLoadingAcao(false);
    }
  };

  const rejeitarSolicitacao = async () => {
    if (!solicitacaoSelecionada || !motivoRejeicao.trim()) {
      showNotification('warning', 'Motivo Obrigatório', 'Por favor, informe o motivo da rejeição.');
      return;
    }

    try {
      setLoadingAcao(true);
      await api.post(`/access-requests/${solicitacaoSelecionada.id}/reject`, {
        rejection_reason: motivoRejeicao.trim()
      });
      
      carregarSolicitacoes();
      fecharModais();
      showNotification('success', 'Solicitação Rejeitada', 'Solicitação rejeitada com sucesso!');
    } catch (error) {
      console.error('Erro ao rejeitar:', error);
      
      // Verificar se é erro de solicitação já processada
      if (error.response?.data?.message?.includes('já foi aprovada')) {
        showNotification('warning', 'Solicitação Já Aprovada', 'Esta solicitação já foi aprovada anteriormente.');
        carregarSolicitacoes(); // Recarregar para atualizar status
      } else if (error.response?.data?.message?.includes('já foi rejeitada')) {
        showNotification('warning', 'Solicitação Já Rejeitada', 'Esta solicitação já foi rejeitada anteriormente.');
        carregarSolicitacoes(); // Recarregar para atualizar status
      } else if (error.response?.data?.message?.includes('Erro ao rejeitar solicitação')) {
        showNotification('error', 'Erro no Servidor', 'Ocorreu um erro interno. Tente novamente ou contate o suporte.');
        carregarSolicitacoes(); // Recarregar para atualizar status
      } else {
        showNotification('error', 'Erro na Rejeição', 'Erro ao rejeitar solicitação. Tente novamente.');
      }
    } finally {
      setLoadingAcao(false);
    }
  };

  const filtrarSolicitacoes = () => {
    if (!busca) return solicitacoes;
    
    const buscaLower = busca.toLowerCase();
    
    return solicitacoes.filter(solicitacao => {
      const fullName = solicitacao.full_name || '';
      const email = solicitacao.corporate_email || '';
      const company = solicitacao.company || '';
      
      return (fullName && String(fullName).toLowerCase().includes(buscaLower)) ||
             (email && String(email).toLowerCase().includes(buscaLower)) ||
             (company && String(company).toLowerCase().includes(buscaLower));
    });
  };

  if (!hasPermission('solicitacoes.view')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Acesso Negado</h1>
          <p className="text-gray-600">Você não tem permissão para acessar esta página.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  const solicitacoesFiltradas = filtrarSolicitacoes();

  return (
    <PageLayout 
      showRefreshButton={true}
      customRefreshFunction={carregarSolicitacoes}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 overflow-x-hidden">
        {/* Filtros e Busca - Mobile Optimized */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="space-y-4">
            {/* Filtro de Status - Mobile First */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Status:</span>
              </div>
              
              {/* Mobile: Dropdown customizado */}
              <div className="relative">
                <button
                  onClick={() => toggleDropdown('status')}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-left bg-white flex items-center justify-between hover:border-gray-400 transition-colors"
                >
                  <span className="font-medium">
                    {filtroStatus === 'pending' ? 'Pendentes' :
                     filtroStatus === 'approved' ? 'Aprovadas' :
                     filtroStatus === 'rejected' ? 'Rejeitadas' :
                     filtroStatus === 'all' ? 'Todas' : 'Pendentes'}
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${dropdownAberto === 'status' ? 'rotate-180' : ''}`} />
                </button>
                
                {dropdownAberto === 'status' && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-[60] max-h-48 overflow-y-auto">
                    {[
                      { value: 'pending', label: 'Pendentes' },
                      { value: 'approved', label: 'Aprovadas' },
                      { value: 'rejected', label: 'Rejeitadas' },
                      { value: 'all', label: 'Todas' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleFiltroChange('status', option.value)}
                        className={`w-full px-4 py-3 text-sm text-left hover:bg-gray-100 transition-colors ${
                          filtroStatus === option.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Busca - Mobile Optimized */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Search className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Buscar:</span>
              </div>
              <input
                type="text"
                placeholder="Nome, e-mail ou empresa..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white hover:border-gray-400 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Lista de Solicitações */}
        <div className="bg-white rounded-lg shadow-sm">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Carregando solicitações...</p>
            </div>
          ) : solicitacoesFiltradas.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma solicitação encontrada</h3>
              <p className="text-gray-600">
                {filtroStatus === 'pending' ? 'Não há solicitações pendentes.' : 
                 filtroStatus === 'approved' ? 'Não há solicitações aprovadas.' :
                 filtroStatus === 'rejected' ? 'Não há solicitações rejeitadas.' :
                 'Não há solicitações para os filtros aplicados.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {solicitacoesFiltradas.map((solicitacao) => (
                <div key={solicitacao.id} className="p-4">
                  {/* Mobile-First Card Design */}
                  <div className="space-y-4">
                    {/* Header com Nome e Status */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-900 text-base truncate">
                            {solicitacao.full_name}
                          </h3>
                          <p className="text-sm text-gray-600 truncate">
                            {solicitacao.corporate_email}
                          </p>
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                        solicitacao.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800'
                          : solicitacao.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {solicitacao.status === 'pending' ? 'Pendente' :
                         solicitacao.status === 'approved' ? 'Aprovada' : 'Rejeitada'}
                      </span>
                    </div>
                    
                    {/* Informações Principais - Layout Mobile Otimizado */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Cargo:</span>
                        <span className="font-medium text-gray-900 text-right flex-1 ml-2">
                          {solicitacao.role || 'N/A'}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Empresa:</span>
                        <span className="font-medium text-gray-900 text-right flex-1 ml-2">
                          {solicitacao.company || 'N/A'}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Data:</span>
                        <span className="font-medium text-gray-900 text-right flex-1 ml-2 flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {new Date(solicitacao.created_at).toLocaleDateString('pt-BR')} às{' '}
                          {new Date(solicitacao.created_at).toLocaleTimeString('pt-BR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                      </div>
                      
                      {/* Informações Adicionais */}
                      {(solicitacao.phone || solicitacao.city) && (
                        <div className="pt-2 border-t border-gray-100 space-y-2">
                          {solicitacao.phone && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-500">Telefone:</span>
                              <span className="font-medium text-gray-900 text-right flex-1 ml-2">
                                {solicitacao.phone}
                              </span>
                            </div>
                          )}
                          {solicitacao.city && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-500">Cidade:</span>
                              <span className="font-medium text-gray-900 text-right flex-1 ml-2">
                                {solicitacao.city}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Motivo da Rejeição */}
                      {solicitacao.rejection_reason && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-800">
                            <strong>Motivo da rejeição:</strong> {solicitacao.rejection_reason}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Ações - Layout Mobile Otimizado */}
                    {solicitacao.status === 'pending' && (
                      <div className="pt-4 border-t border-gray-100">
                        <div className="grid grid-cols-1 gap-2">
                          {/* Botão Configurar Permissões */}
                          <button
                            onClick={() => abrirModalPermissoes(solicitacao)}
                            className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-colors text-sm font-medium ${
                              permissoesPorSolicitacao[solicitacao.id] && 
                              (permissoesPorSolicitacao[solicitacao.id].permissoes.length > 0 || 
                               permissoesPorSolicitacao[solicitacao.id].empresas.length > 0)
                                ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300'
                            }`}
                          >
                            <Settings className="h-4 w-4" />
                            <span>
                              {permissoesPorSolicitacao[solicitacao.id] && 
                               (permissoesPorSolicitacao[solicitacao.id].permissoes.length > 0 || 
                                permissoesPorSolicitacao[solicitacao.id].empresas.length > 0)
                                ? 'Permissões Configuradas'
                                : 'Configurar Permissões'
                              }
                            </span>
                          </button>
                          
                          {/* Botões de Ação em Grid */}
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => abrirModalAprovar(solicitacao)}
                              className="flex items-center justify-center space-x-2 bg-green-100 text-green-700 px-4 py-3 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium border border-green-300"
                            >
                              <CheckCircle className="h-4 w-4" />
                              <span>Aprovar</span>
                            </button>
                            
                            <button
                              onClick={() => abrirModalRejeitar(solicitacao)}
                              className="flex items-center justify-center space-x-2 bg-red-100 text-red-700 px-4 py-3 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium border border-red-300"
                            >
                              <XCircle className="h-4 w-4" />
                              <span>Rejeitar</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Paginação */}
        {totalPaginas > 1 && (
          <div className="mt-6 flex justify-center">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2">
              <button
                onClick={() => setPagina(p => Math.max(1, p - 1))}
                disabled={pagina === 1}
                className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm w-full sm:w-auto"
              >
                Anterior
              </button>
              
              <span className="px-4 py-2 text-gray-600 text-sm text-center">
                Página {pagina} de {totalPaginas}
              </span>
              
              <button
                onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
                disabled={pagina === totalPaginas}
                className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm w-full sm:w-auto"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Aprovação */}
      <Modal
        isOpen={modalAprovar}
        onClose={fecharModais}
        title="Confirmar Aprovação"
        size="md"
      >
        {solicitacaoSelecionada && (
          <div>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Você está prestes a aprovar a solicitação de acesso para:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">{solicitacaoSelecionada.full_name}</p>
                <p className="text-gray-600">{solicitacaoSelecionada.corporate_email}</p>
                <p className="text-gray-600">Cargo: {solicitacaoSelecionada.role}</p>
                <p className="text-gray-600">Empresa: {solicitacaoSelecionada.company || 'N/A'}</p>
              </div>
            </div>

            {/* Resumo das Permissões Configuradas */}
            {(permissoesSelecionadas.length > 0 || empresasSelecionadas.length > 0) && (
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-3">Permissões Configuradas</h4>
                <PermissionSummary 
                  permissions={permissoesSelecionadas}
                  companies={empresasSelecionadas}
                  className="bg-blue-50 border border-blue-200"
                />
              </div>
            )}

            {/* Aviso se não há permissões configuradas */}
            {permissoesSelecionadas.length === 0 && empresasSelecionadas.length === 0 && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <Shield className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                  <div>
                    <h4 className="font-medium text-yellow-900">Nenhuma permissão configurada</h4>
                    <p className="text-sm text-yellow-800 mt-1">
                      O usuário será criado com as permissões padrão do cargo solicitado. 
                      Você pode configurar permissões específicas antes de aprovar.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={fecharModais}
                disabled={loadingAcao}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={aprovarSolicitacao}
                disabled={loadingAcao}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {loadingAcao ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processando...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    <span>Confirmar Aprovação</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Rejeição */}
      <Modal
        isOpen={modalRejeitar}
        onClose={fecharModais}
        title="Confirmar Rejeição"
        size="md"
      >
        {solicitacaoSelecionada && (
          <div>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Você está prestes a rejeitar a solicitação de acesso para:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="font-semibold text-gray-900">{solicitacaoSelecionada.full_name}</p>
                <p className="text-gray-600">{solicitacaoSelecionada.corporate_email}</p>
                <p className="text-gray-600">Cargo: {solicitacaoSelecionada.role}</p>
                <p className="text-gray-600">Empresa: {solicitacaoSelecionada.company || 'N/A'}</p>
              </div>
              
              <div>
                <label htmlFor="motivoRejeicao" className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo da rejeição *
                </label>
                <textarea
                  id="motivoRejeicao"
                  value={motivoRejeicao}
                  onChange={(e) => setMotivoRejeicao(e.target.value)}
                  placeholder="Informe o motivo da rejeição..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows="3"
                  maxLength="500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {motivoRejeicao.length}/500 caracteres
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={fecharModais}
                disabled={loadingAcao}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={rejeitarSolicitacao}
                disabled={loadingAcao || !motivoRejeicao.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {loadingAcao ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processando...</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    <span>Confirmar Rejeição</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Configuração de Permissões */}
      <Modal
        isOpen={modalPermissoes}
        onClose={fecharModais}
        title="Configurar Permissões de Acesso"
        size="xl"
      >
        {solicitacaoSelecionada && (
          <div>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Configure as permissões e acesso por empresa para:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">{solicitacaoSelecionada.full_name}</p>
                <p className="text-gray-600">{solicitacaoSelecionada.corporate_email}</p>
                <p className="text-gray-600">Cargo: {solicitacaoSelecionada.role}</p>
                <p className="text-gray-600">Empresa: {solicitacaoSelecionada.company || 'N/A'}</p>
              </div>
            </div>
            
            <PermissionSelector
              userId={solicitacaoSelecionada.id}
              onPermissionsChange={handlePermissoesChange}
              initialPermissions={permissoesSelecionadas}
              initialCompanies={empresasSelecionadas}
            />
            
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <Shield className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                <div>
                  <h4 className="font-medium text-blue-900">Como funciona o sistema de permissões</h4>
                  <ul className="text-sm text-blue-800 mt-2 space-y-1">
                    <li>• <strong>Empresas:</strong> Defina quais empresas o usuário pode acessar</li>
                    <li>• <strong>Permissões:</strong> Defina o que o usuário pode fazer em cada área</li>
                    <li>• <strong>Segurança:</strong> O usuário só verá dados das empresas selecionadas</li>
                    <li>• <strong>Flexibilidade:</strong> Permissões podem ser ajustadas individualmente</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={limparPermissoes}
                disabled={loadingPermissoes}
                className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50"
              >
                Limpar Seleções
              </button>
              
              <div className="flex space-x-3">
                <button
                  onClick={fecharModais}
                  disabled={loadingPermissoes}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    // Salvar configurações e fechar modal
                    fecharModais();
                    showNotification('success', 'Configurações Salvas', 'Configurações salvas! Agora você pode aprovar a solicitação.');
                  }}
                  disabled={loadingPermissoes}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {loadingPermissoes ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <Settings className="h-4 w-4" />
                      <span>Salvar Configurações</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Notificação */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </PageLayout>
  );
};

export default GestaoSolicitacoes;
